import { useEffect, useMemo, useState } from 'react';
import api, { API } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Users, BarChart3, Shield, ShieldCheck, Search, RefreshCw, Loader2,
  KeyRound, Ban, Crown, BadgeCheck, ScrollText, Layers, Plus, Trash2, Save, X, Lock,
  Scale, TrendingUp, TrendingDown, CheckCircle2, XCircle, Sparkles, Coins, Download,
  Eye, Check,
} from 'lucide-react';

const TABS = [
  { key: 'overview',   label: 'Overview',  icon: BarChart3, perm: 'analytics.view' },
  { key: 'balance',    label: 'Balance',   icon: Scale,     perm: 'analytics.view' },
  { key: 'users',      label: 'Users',     icon: Users,     perm: 'users.view' },
  { key: 'roles',      label: 'Roles',     icon: Layers,    perm: 'roles.view' },
  { key: 'audit',      label: 'Audit Log', icon: ScrollText, perm: 'auditlog.view' },
];


export default function Admin() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [tab, setTab] = useState('overview');

  const reloadMe = () => {
    setLoading(true);
    api.get('/admin/me')
      .then(r => { setMe(r.data); setForbidden(false); })
      .catch(err => {
        if (err?.response?.status === 403 || err?.response?.status === 401) setForbidden(true);
        else toast.error('Failed to load admin session');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { reloadMe(); }, []);

  const perms = useMemo(() => new Set(me?.permissions || []), [me]);
  const has = (p) => perms.has(p) || perms.has('*');

  if (loading) return (
    <div className="p-10 text-center" data-testid="admin-loading">
      <Loader2 className="w-5 h-5 animate-spin mx-auto text-accent" />
    </div>
  );

  if (forbidden) return (
    <div className="rounded-3xl bg-card border border-white/10 p-8 text-center" data-testid="admin-forbidden">
      <Lock className="w-8 h-8 text-accent mx-auto mb-3" strokeWidth={1.5} />
      <h2 className="font-serif text-2xl">Admin only</h2>
      <p className="text-sm text-muted-foreground mt-2">
        Your account lacks admin privileges. Ask the Bootstrap Administrator for role assignment.
      </p>
    </div>
  );

  const visibleTabs = TABS.filter(t => has(t.perm) || me?.is_bootstrap_admin);
  if (visibleTabs.length === 0) return (
    <div className="rounded-3xl bg-card border border-white/10 p-8 text-center" data-testid="admin-forbidden">
      <Lock className="w-8 h-8 text-accent mx-auto mb-3" strokeWidth={1.5} />
      <h2 className="font-serif text-2xl">Admin only</h2>
      <p className="text-sm text-muted-foreground mt-2">
        Your account has no admin permissions. Ask the Bootstrap Administrator for role assignment.
      </p>
    </div>
  );
  const activeTab = visibleTabs.find(t => t.key === tab) || visibleTabs[0];

  return (
    <div className="space-y-5" data-testid="admin-root">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Control Room</p>
          <h2 className="font-serif text-3xl leading-tight">Admin Portal</h2>
          <p className="text-xs text-muted-foreground mt-1">
            {me?.user?.name} · <span className="font-mono text-[10px]">{me?.user?.email}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {me?.is_bootstrap_admin && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-400/30 text-[10px] uppercase tracking-widest text-amber-300" data-testid="badge-bootstrap-admin">
              <Crown className="w-3 h-3" /> Bootstrap Admin
            </span>
          )}
          {(me?.roles || []).map(r => (
            <span key={r.role_id} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass border border-emerald-400/30 text-[10px] uppercase tracking-widest text-emerald-300">
              <ShieldCheck className="w-3 h-3" /> {r.name}
            </span>
          ))}
          <button
            data-testid="link-ai-explorer-admin"
            onClick={() => window.location.assign('/admin/ai-explorers')}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-white text-[10px] uppercase tracking-widest hover:brightness-110"
          >
            AI Explorers
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="admin-tab-bar">
        {visibleTabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`flex-shrink-0 flex items-center gap-2 text-[11px] uppercase tracking-widest px-3 py-2 rounded-full border transition-colors ${
                activeTab.key === t.key
                  ? 'border-accent/60 bg-accent/10 text-accent'
                  : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
              }`}
            ><Icon className="w-3 h-3" /> {t.label}</button>
          );
        })}
      </div>

      {activeTab.key === 'overview' && <OverviewPane />}
      {activeTab.key === 'balance'  && <BalancePane />}
      {activeTab.key === 'users'    && <UsersPane me={me} has={has} onChange={reloadMe} />}
      {activeTab.key === 'roles'    && <RolesPane me={me} has={has} />}
      {activeTab.key === 'audit'    && <AuditPane />}
    </div>
  );
}


// -----------------------------------------------------------------------
// Balance Intelligence (Battle Engine Phase 3)
// -----------------------------------------------------------------------
function BalancePane() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [since, setSince] = useState(30);
  const [tab, setTab] = useState('recs');

  const load = () => {
    setBusy(true);
    api.get(`/admin/balance/overview?since_days=${since}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load balance data'))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [since]);

  const recompute = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/balance/recompute');
      toast.success(`Recomputed — ${r.data.created} new recommendation${r.data.created === 1 ? '' : 's'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Recompute failed'); setBusy(false); }
  };

  const decide = async (rec_id, decision) => {
    try {
      const r = await api.post(`/admin/balance/recommendations/${rec_id}/${decision}`);
      toast.success(`Recommendation ${r.data.status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (busy && !data) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;
  if (!data) return null;

  const subtabs = [
    { key: 'recs',     label: `Recommendations (${data.recommendations.pending.length})` },
    { key: 'species',  label: `Species (${data.species.length})` },
    { key: 'cards',    label: `Cards (${data.cards.length})` },
    { key: 'abilities',label: `Abilities (${data.abilities.length})` },
    { key: 'stage',    label: 'Arena / Weather' },
    { key: 'active',   label: 'Active Tuning' },
  ];

  return (
    <div className="space-y-4" data-testid="pane-balance">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Window</div>
        {[7, 30, 90, 365].map(n => (
          <button key={n} onClick={() => setSince(n)} data-testid={`balance-window-${n}`}
            className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
              since === n ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
            }`}
          >{n === 365 ? 'All time' : `${n}d`}</button>
        ))}
        <button onClick={recompute} data-testid="btn-balance-recompute"
          className="ml-auto text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Recompute
        </button>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="balance-subtabs">
        {subtabs.map(s => (
          <button key={s.key} onClick={() => setTab(s.key)}
            data-testid={`balance-tab-${s.key}`}
            className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              tab === s.key ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
            }`}
          >{s.label}</button>
        ))}
      </div>

      {tab === 'recs' && <RecommendationsList recs={data.recommendations} onDecide={decide} />}
      {tab === 'species' && <RatingsTable rows={data.species} testid="species-table" />}
      {tab === 'cards' && <RatingsTable rows={data.cards} testid="cards-table" showName />}
      {tab === 'abilities' && <AbilitiesTable rows={data.abilities} />}
      {tab === 'stage' && <StageTable arena={data.arena} weather={data.weather} season={data.season} />}
      {tab === 'active' && <ActiveTuning tuning={data.active_tuning} />}
    </div>
  );
}


function RecommendationsList({ recs, onDecide }) {
  const pending = recs.pending || [];
  const approved = recs.approved || [];
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Pending review</p>
        {pending.length === 0 && <p className="text-xs text-muted-foreground">No pending recommendations — the ecosystem looks balanced.</p>}
        <div className="space-y-2">
          {pending.map(r => (
            <div key={r.recommendation_id} data-testid="rec-row-pending"
              className="rounded-2xl bg-card border border-white/5 p-3 flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                r.direction === 'buff' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'
              }`}>
                {r.direction === 'buff' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md ${
                    r.direction === 'buff' ? 'bg-emerald-500/10 border border-emerald-400/30 text-emerald-200'
                                            : 'bg-red-500/10 border border-red-400/30 text-red-200'
                  }`}>{r.direction}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">{r.scope}·{r.key}</span>
                </div>
                <p className="text-xs mt-1">{r.rationale}</p>
                <p className="text-[10px] font-mono text-muted-foreground mt-1">
                  {Object.entries(r.tuning || {}).map(([k, v]) => `${k}=${v}`).join(' · ')}
                </p>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button onClick={() => onDecide(r.recommendation_id, 'approve')}
                  data-testid="btn-rec-approve"
                  className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 flex items-center gap-1"
                ><CheckCircle2 className="w-3 h-3" /> Approve</button>
                <button onClick={() => onDecide(r.recommendation_id, 'reject')}
                  data-testid="btn-rec-reject"
                  className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-muted-foreground flex items-center gap-1"
                ><XCircle className="w-3 h-3" /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {approved.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Applied</p>
          <div className="space-y-1.5">
            {approved.map(r => (
              <div key={r.recommendation_id} data-testid="rec-row-approved"
                className="rounded-xl bg-black/30 border border-emerald-400/20 p-2.5 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] font-mono text-emerald-200">{r.scope}·{r.key}</span>
                <span className="text-[10px] text-muted-foreground truncate">{r.rationale}</span>
                <span className="ml-auto text-[10px] font-mono text-emerald-300">
                  {Object.entries(r.tuning || {}).map(([k, v]) => `${k}=${v}`).join(' · ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function RatingsTable({ rows, testid, showName }) {
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">No data yet — play more battles to populate this view.</p>;
  return (
    <div className="rounded-2xl bg-card border border-white/5 overflow-hidden" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-black/30 text-[9px] uppercase tracking-widest text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">{showName ? 'Card' : 'Key'}</th>
            <th className="px-2 py-2 text-right">Battles</th>
            <th className="px-2 py-2 text-right">Win %</th>
            <th className="px-2 py-2 text-right">Acc</th>
            <th className="px-2 py-2 text-right">Crit</th>
            <th className="px-2 py-2 text-right">Avg Dmg</th>
            <th className="px-2 py-2 text-right">Power</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((r, i) => (
            <tr key={r.key + i} data-testid="rating-row">
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <span>{showName ? (r.name || r.key) : r.key}</span>
                  {showName && r.rarity && <span className="text-[9px] uppercase tracking-widest text-muted-foreground">· {r.rarity}</span>}
                </div>
              </td>
              <td className="px-2 py-2 font-mono tabular-nums text-right">{r.battles}</td>
              <td className="px-2 py-2 font-mono tabular-nums text-right">{Math.round(r.win_rate * 100)}%</td>
              <td className="px-2 py-2 font-mono tabular-nums text-right text-muted-foreground">{r.accuracy != null ? Math.round(r.accuracy * 100) + '%' : '—'}</td>
              <td className="px-2 py-2 font-mono tabular-nums text-right text-muted-foreground">{r.crit_rate != null ? Math.round(r.crit_rate * 100) + '%' : '—'}</td>
              <td className="px-2 py-2 font-mono tabular-nums text-right text-muted-foreground">{r.avg_damage ?? '—'}</td>
              <td className={`px-2 py-2 font-mono tabular-nums text-right ${
                r.power_score > 16 ? 'text-red-300' : r.power_score < -16 ? 'text-cyan-300' : 'text-white/80'
              }`}>{r.power_score > 0 ? `+${r.power_score}` : r.power_score}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function AbilitiesTable({ rows }) {
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">No ability usage recorded yet.</p>;
  return (
    <div className="rounded-2xl bg-card border border-white/5 overflow-hidden" data-testid="abilities-table">
      <table className="w-full text-xs">
        <thead className="bg-black/30 text-[9px] uppercase tracking-widest text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Ability keyword</th>
            <th className="px-2 py-2 text-left">Kind</th>
            <th className="px-2 py-2 text-right">Cooldown</th>
            <th className="px-2 py-2 text-right">Combo key</th>
            <th className="px-2 py-2 text-right">Fires</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((r, i) => (
            <tr key={r.key + i}>
              <td className="px-3 py-2 font-mono">{r.key === '__combos__' ? '(combos fired)' : r.key}</td>
              <td className="px-2 py-2 text-muted-foreground">{r.kind}</td>
              <td className="px-2 py-2 font-mono tabular-nums text-right text-muted-foreground">{r.cooldown}</td>
              <td className="px-2 py-2 font-mono text-right text-muted-foreground">{r.combo_key}</td>
              <td className="px-2 py-2 font-mono tabular-nums text-right">{r.key === '__combos__' ? r.combo_triggers : r.fires}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function StageSection({ title, rows, testid }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{title}</p>
      {rows.length === 0 ? <p className="text-xs text-muted-foreground">—</p> : (
        <div className="rounded-2xl bg-card border border-white/5 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-black/30 text-[9px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">{title}</th>
                <th className="px-2 py-2 text-right">Battles</th>
                <th className="px-2 py-2 text-right">CH Win %</th>
                <th className="px-2 py-2 text-right">Avg Rounds</th>
                <th className="px-2 py-2 text-right">Bias</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map(r => (
                <tr key={r.key}>
                  <td className="px-3 py-2 capitalize">{r.key}</td>
                  <td className="px-2 py-2 font-mono tabular-nums text-right">{r.battles}</td>
                  <td className="px-2 py-2 font-mono tabular-nums text-right">{Math.round(r.challenger_win_rate * 100)}%</td>
                  <td className="px-2 py-2 font-mono tabular-nums text-right text-muted-foreground">{r.avg_rounds}</td>
                  <td className={`px-2 py-2 font-mono tabular-nums text-right ${Math.abs(r.bias) > 0.1 ? 'text-amber-300' : 'text-white/70'}`}>{r.bias > 0 ? `+${r.bias}` : r.bias}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function StageTable({ arena, weather, season }) {
  return (
    <div className="space-y-4">
      <StageSection title="Arena" rows={arena} testid="stage-arena" />
      <StageSection title="Weather" rows={weather} testid="stage-weather" />
      <StageSection title="Season" rows={season} testid="stage-season" />
    </div>
  );
}


function ActiveTuning({ tuning }) {
  const scopes = Object.entries(tuning || {}).filter(([, v]) => v && Object.keys(v).length > 0);
  if (scopes.length === 0) return <p className="text-xs text-muted-foreground" data-testid="active-tuning-empty">No approved tuning is currently active.</p>;
  return (
    <div className="space-y-3" data-testid="active-tuning">
      {scopes.map(([scope, items]) => (
        <div key={scope} className="rounded-2xl bg-card border border-white/5 p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{scope}</p>
          <div className="space-y-1">
            {Object.entries(items).map(([k, mults]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="font-mono text-white/80 capitalize">{k}</span>
                <span className="font-mono text-emerald-300">
                  {Object.entries(mults).filter(([kk]) => !kk.startsWith('_')).map(([kk, vv]) => `${kk}=${vv}`).join(' · ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}



// -----------------------------------------------------------------------
// Overview
// -----------------------------------------------------------------------
function OverviewPane() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  // Lookup query is hoisted here so `StripeEventsPanel` can trigger a
  // search on any user_id via one-click event resolution.
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupTick, setLookupTick] = useState(0);
  useEffect(() => {
    api.get('/admin/overview').then(r => setData(r.data)).catch(() => setErr(true));
  }, []);
  if (err) return <p className="text-xs text-muted-foreground">You don&apos;t have permission to view analytics.</p>;
  if (!data) return <Loader2 className="w-4 h-4 animate-spin text-accent" />;

  const resolveUser = (userId) => {
    if (!userId) return;
    setLookupQuery(userId);
    setLookupTick(t => t + 1);
    // Give the lookup panel a moment to search, then scroll to it.
    setTimeout(() => {
      document.querySelector('[data-testid=user-credit-lookup-panel]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  return (
    <div className="space-y-4" data-testid="pane-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Users"     value={data.users.total}     testid="stat-users-total" />
        <StatCard label="Active"          value={data.users.active}    testid="stat-users-active" />
        <StatCard label="Suspended"       value={data.users.suspended} testid="stat-users-suspended" />
        <StatCard label="Observations"    value={data.observations}    testid="stat-observations" />
        <StatCard label="Battle Cards"    value={data.battle_cards}    testid="stat-battle-cards" />
        <StatCard label="Battles Fought"  value={data.battles}         testid="stat-battles" />
        <StatCard label="Active Listings" value={data.active_listings} testid="stat-listings" />
      </div>

      <div className="rounded-3xl bg-card border border-white/5 p-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Role distribution</p>
        <div className="space-y-1.5" data-testid="role-counts">
          {(data.role_counts || []).map(r => (
            <div key={r.role_id} className="flex items-center justify-between text-xs" data-testid={`role-count-${r.key}`}>
              <span className="text-white/80">{r.name} <span className="text-muted-foreground text-[10px] ml-1">({r.key})</span></span>
              <span className="font-mono text-accent">{r.user_count}</span>
            </div>
          ))}
        </div>
      </div>

      <CreditsReconcilePanel />
      <TrailheadBackfillPanel />
      <RefreshLogPanel />
      <SubscriptionHealthPanel onOpenUser={resolveUser} />
      <StripeEventsPanel onOpenUser={resolveUser} />
      <UserCreditLookupPanel initialQuery={lookupQuery} triggerTick={lookupTick} />
    </div>
  );
}

// -----------------------------------------------------------------------
// Stripe Events Feed — recent refunds / disputes / failed charges
// -----------------------------------------------------------------------
function StripeEventsPanel({ onOpenUser }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/stripe/events?limit=15')
      .then(r => { setData(r.data); setForbidden(false); })
      .catch(err => { if (err?.response?.status === 403) setForbidden(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (forbidden) return null;

  const events = data?.events || [];
  const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };
  const fmtAmount = (cents, currency) => {
    if (cents == null) return '—';
    const v = (cents / 100).toFixed(2);
    const sym = (currency || 'usd').toUpperCase();
    return `${v} ${sym}`;
  };
  const kindMeta = {
    'charge.refunded':         { label: 'Refund',   color: 'text-amber-300',   bg: 'bg-amber-500/10',  border: 'border-amber-500/25' },
    'charge.dispute.created':  { label: 'Dispute',  color: 'text-red-300',     bg: 'bg-red-500/10',    border: 'border-red-500/25' },
    'charge.failed':           { label: 'Failed',   color: 'text-red-300',     bg: 'bg-red-500/10',    border: 'border-red-500/25' },
    'invoice.payment_failed':  { label: 'Invoice ✗', color: 'text-fuchsia-300', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/25' },
  };

  return (
    <div className="rounded-3xl bg-card border border-white/5 p-4" data-testid="stripe-events-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
          <TrendingDown className="w-4 h-4 text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Stripe Events · Billing Attention</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Live feed of refunds, disputes and failed charges. Surfaces complaints before users email you.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="stripe-events-refresh"
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"
          aria-label="Refresh Stripe events"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {loading && !data ? (
        <p className="text-[10px] text-muted-foreground text-center py-4">Loading…</p>
      ) : data?.available === false ? (
        <div className="rounded-xl bg-black/25 border border-white/5 p-3 text-[10px] text-muted-foreground" data-testid="stripe-events-unavailable">
          Stripe events feed unavailable: <span className="text-white/60">{data?.reason || 'unknown'}</span>
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-3 text-[10px] text-emerald-300 flex items-center gap-2" data-testid="stripe-events-empty">
          <CheckCircle2 className="w-3.5 h-3.5" /> Nothing to worry about — no recent refunds, disputes or failed charges.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="stripe-events-list">
          {events.map(ev => {
            const meta = kindMeta[ev.type] || { label: ev.type, color: 'text-white/70', bg: 'bg-white/5', border: 'border-white/10' };
            const clickable = !!(ev.user_id && onOpenUser);
            const RowTag = clickable ? 'button' : 'div';
            return (
              <RowTag
                key={ev.id}
                onClick={clickable ? () => onOpenUser(ev.user_id) : undefined}
                data-testid={`stripe-event-${ev.id}`}
                className={`w-full text-left rounded-xl border p-2.5 ${meta.bg} ${meta.border} ${clickable ? 'cursor-pointer hover:brightness-125 transition' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-[9px] font-semibold uppercase tracking-widest w-16 ${meta.color}`}>{meta.label}</span>
                  <span className="font-mono text-xs">{fmtAmount(ev.amount_cents, ev.currency)}</span>
                  <div className="flex-1 min-w-0 text-[10px] truncate">
                    {ev.user_name ? (
                      <span className="text-white/80">{ev.user_name}</span>
                    ) : (
                      <span className="text-muted-foreground">unknown user</span>
                    )}
                    {ev.user_email && <span className="text-muted-foreground ml-1">· {ev.user_email}</span>}
                  </div>
                  {clickable && (
                    <span className="text-[9px] uppercase tracking-widest text-accent whitespace-nowrap">
                      Resolve →
                    </span>
                  )}
                  <span className="text-[9px] text-muted-foreground whitespace-nowrap">{fmtTime(ev.created_at)}</span>
                </div>
                {ev.reason && (
                  <p className="text-[10px] text-muted-foreground mt-1 pl-16 text-left">{ev.reason}</p>
                )}
              </RowTag>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Subscription Health — MRR, tier counts, churn, top purchasers
// -----------------------------------------------------------------------
function SubscriptionHealthPanel({ onOpenUser }) {
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    api.get('/admin/credits/health')
      .then(r => { setData(r.data); setForbidden(false); })
      .catch(err => { if (err?.response?.status === 403) setForbidden(true); });
  }, []);

  if (forbidden) return null;
  if (!data) return null;

  const tiers = data.tier_counts || {};
  const tierMeta = [
    { key: 'trailhead',  label: 'Trailhead',  color: 'text-white/70' },
    { key: 'explorer',   label: 'Explorer',   color: 'text-emerald-300' },
    { key: 'naturalist', label: 'Naturalist', color: 'text-amber-300' },
    { key: 'pathfinder', label: 'Pathfinder', color: 'text-fuchsia-300' },
  ];

  return (
    <div className="rounded-3xl bg-card border border-white/5 p-4" data-testid="subscription-health-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Subscription Health</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Live tier counts · MRR · churn · pack revenue
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {tierMeta.map(m => (
          <div key={m.key} className="rounded-xl bg-black/25 border border-white/5 p-2.5"
               data-testid={`health-tier-${m.key}`}>
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{m.label}</p>
            <p className={`font-mono text-lg mt-1 ${m.color}`}>{tiers[m.key] ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/25 p-2.5"
             data-testid="health-mrr">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Monthly recurring</p>
          <p className="font-mono text-lg mt-1 text-emerald-300">${(data.mrr_usd ?? 0).toFixed(2)}</p>
          <p className="text-[9px] text-muted-foreground mt-0.5">USD / month</p>
        </div>
        <div className={`rounded-xl border p-2.5 ${(data.churned_30d ?? 0) > 0 ? 'bg-red-500/10 border-red-500/25' : 'bg-black/25 border-white/5'}`}
             data-testid="health-churn">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Churn 30d</p>
          <p className={`font-mono text-lg mt-1 ${(data.churned_30d ?? 0) > 0 ? 'text-red-300' : ''}`}>
            {data.churned_30d ?? 0}
          </p>
          <p className="text-[9px] text-muted-foreground mt-0.5">stripe-canceled</p>
        </div>
        <div className="rounded-xl bg-black/25 border border-white/5 p-2.5"
             data-testid="health-pack-revenue">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Pack revenue</p>
          <p className="font-mono text-lg mt-1 text-amber-300">${(data.total_pack_revenue_usd ?? 0).toFixed(2)}</p>
          <p className="text-[9px] text-muted-foreground mt-0.5">
            {data.total_credits_sold ?? 0} credits sold
          </p>
        </div>
      </div>

      {data.top_purchasers?.length > 0 && (
        <div data-testid="health-top-purchasers">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Top pack purchasers
          </p>
          <div className="space-y-1.5">
            {data.top_purchasers.map((u, i) => {
              const clickable = !!onOpenUser;
              const RowTag = clickable ? 'button' : 'div';
              return (
                <RowTag
                  key={u.user_id}
                  onClick={clickable ? () => onOpenUser(u.user_id) : undefined}
                  className={`w-full text-left flex items-center gap-3 rounded-xl bg-black/20 border border-white/5 px-3 py-2 ${clickable ? 'cursor-pointer hover:bg-black/30 transition' : ''}`}
                  data-testid={`top-purchaser-${u.user_id}`}
                >
                  <span className="font-mono text-xs text-muted-foreground w-4">#{i + 1}</span>
                  <img
                    src={u.picture || `https://i.pravatar.cc/32?u=${u.user_id}`}
                    alt=""
                    className={`w-7 h-7 rounded-full object-cover ${u.is_founder ? 'ring-2' : 'border border-white/10'}`}
                    style={u.is_founder ? { boxShadow: '0 0 0 1px #E9C46A', outline: 'none' } : {}}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate">{u.name}</p>
                    <p className="text-[9px] text-muted-foreground font-mono truncate">{u.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-accent">{u.credits_purchased} cr</p>
                    <p className="text-[9px] text-muted-foreground">{u.packs_bought} pack{u.packs_bought === 1 ? '' : 's'}</p>
                  </div>
                </RowTag>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// User Credit Lookup — search + ledger + force-refresh
// -----------------------------------------------------------------------
function UserCreditLookupPanel({ initialQuery = '', triggerTick = 0 }) {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  const doSearch = async (query) => {
    const target = (query ?? q).trim();
    if (target.length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get('/admin/credits/user', { params: { q: target } });
      setMatches(data.matches || []);
      if (!data.matches?.length) toast.message('No explorers matched that search');
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error(err?.response?.data?.detail || 'Lookup failed');
    } finally {
      setSearching(false);
    }
  };

  const search = () => doSearch();

  // One-click resolve from a Stripe event / top-purchaser row.
  useEffect(() => {
    if (triggerTick > 0 && initialQuery) {
      setQ(initialQuery);
      doSearch(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerTick]);

  const forceRefresh = async (userId) => {
    setRefreshingId(userId);
    try {
      const { data } = await api.post(`/admin/credits/user/${userId}/refresh`);
      toast.success(`Refreshed ${data.tier} · +${data.monthly} credits`);
      // Re-run the search so the ledger + balances update in place.
      search();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshingId(null);
    }
  };

  if (forbidden) return null;

  return (
    <div className="rounded-3xl bg-card border border-white/5 p-4" data-testid="user-credit-lookup-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg bg-fuchsia-500/10 flex items-center justify-center flex-shrink-0">
          <Search className="w-4 h-4 text-fuchsia-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">User Credit Lookup</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Inspect an explorer's ledger and force-refresh their monthly allowance
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Email, user_id, name or nickname…"
          data-testid="user-lookup-input"
          className="flex-1 bg-black/25 border border-white/10 rounded-xl px-3 py-2 text-xs placeholder:text-muted-foreground focus:border-accent/60 outline-none"
        />
        <button
          onClick={search}
          disabled={searching || q.trim().length < 2}
          data-testid="user-lookup-search-btn"
          className="px-4 py-2 rounded-xl bg-accent text-black text-xs font-semibold hover:bg-accent/90 disabled:opacity-50 flex items-center gap-1.5"
        >
          {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Search
        </button>
      </div>

      <div className="space-y-3">
        {matches.map(u => (
          <UserCreditCard
            key={u.user_id}
            user={u}
            onForceRefresh={() => forceRefresh(u.user_id)}
            onReload={search}
            refreshing={refreshingId === u.user_id}
          />
        ))}
      </div>
    </div>
  );
}

function UserCreditCard({ user, onForceRefresh, onReload, refreshing }) {
  const canForce = !['trailhead', 'admin'].includes(user.tier);
  const [granting, setGranting] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const c = user.credits || {};
  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const grantPack = async (packId, reason) => {
    setGranting(true);
    try {
      const { data } = await api.post(
        `/admin/credits/user/${user.user_id}/grant-pack`,
        { pack_id: packId, reason },
      );
      toast.success(`Granted +${data.credits_granted} credits (${data.pack_id})`);
      setShowGrant(false);
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Grant failed');
    } finally {
      setGranting(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const resp = await api.get(
        `/admin/credits/user/${user.user_id}/ledger.csv`,
        { responseType: 'blob' },
      );
      // Pull a friendly filename out of the Content-Disposition header,
      // fall back to a timestamp-based one if the header is missing.
      const disp = resp.headers['content-disposition'] || '';
      const m = disp.match(/filename="?([^"]+)"?/i);
      const filename = m?.[1] || `wildquest-ledger-${user.user_id}.csv`;
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Ledger CSV downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="rounded-2xl bg-black/25 border border-white/10 p-3" data-testid={`user-credit-card-${user.user_id}`}>
      <div className="flex items-center gap-3">
        <img
          src={user.picture || `https://i.pravatar.cc/40?u=${user.user_id}`}
          alt=""
          className="w-10 h-10 rounded-full object-cover"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate flex items-center gap-1.5">
            {user.nickname || user.name}
            {user.is_founder && <span title="Founder" style={{ color: '#E9C46A' }} className="text-[10px]">★</span>}
          </p>
          <p className="text-[10px] text-muted-foreground font-mono truncate">{user.email}</p>
        </div>
        <div className="text-right">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{user.tier_config?.label || user.tier}</p>
          <p className="text-[9px] text-muted-foreground">
            {user.tier_source || 'default'} · since {fmt(user.tier_since)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5 mt-3">
        <MiniStat label="Monthly" value={c.monthly ?? 0} />
        <MiniStat label="Purchased" value={c.purchased ?? 0} />
        <MiniStat label="Lifetime" value={c.lifetime ?? 0} />
        <MiniStat label="Total" value={c.is_admin_unlimited ? '∞' : (c.total ?? 0)} accent />
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2 text-[10px]">
        <div className="text-muted-foreground">Cycle start: <span className="font-mono text-white/70">{fmt(user.credits_cycle_start)}</span></div>
        <div className="text-muted-foreground">Cycle end: <span className="font-mono text-white/70">{fmt(user.credits_cycle_end)}</span></div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        {canForce ? (
          <button
            onClick={onForceRefresh}
            disabled={refreshing}
            data-testid={`force-refresh-${user.user_id}`}
            className="py-2 rounded-xl bg-primary text-white text-xs font-medium hover:bg-primary/80 disabled:opacity-60 flex items-center justify-center gap-1.5"
          >
            {refreshing ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Refreshing…</>
            ) : (
              <><RefreshCw className="w-3.5 h-3.5" /> Force refresh</>
            )}
          </button>
        ) : (
          <div />
        )}
        <button
          onClick={() => setShowGrant(v => !v)}
          data-testid={`grant-pack-toggle-${user.user_id}`}
          className={`py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 border ${showGrant ? 'bg-accent text-black border-accent' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/85'}`}
        >
          <Sparkles className="w-3.5 h-3.5" /> Grant pack
        </button>
      </div>

      {showGrant && (
        <GrantPackForm onCancel={() => setShowGrant(false)}
                        onGrant={grantPack}
                        granting={granting}
                        userId={user.user_id} />
      )}

      {user.ledger?.length > 0 && (
        <details className="mt-3" data-testid={`ledger-${user.user_id}`}>
          <summary className="text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-white/70 flex items-center justify-between">
            <span>Recent ledger ({user.ledger.length})</span>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); exportCsv(); }}
              disabled={exporting}
              data-testid={`export-csv-${user.user_id}`}
              className="text-[10px] normal-case tracking-normal px-2 py-0.5 rounded-md bg-white/5 hover:bg-white/10 text-white/70 flex items-center gap-1"
              title="Export full ledger as CSV"
            >
              {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              CSV
            </button>
          </summary>
          <div className="mt-2 space-y-0.5 max-h-40 overflow-y-auto pr-1">
            {user.ledger.map((row, i) => (
              <div key={i} className="flex items-center justify-between text-[10px] font-mono py-1 border-b border-white/5 last:border-0">
                <span className="text-muted-foreground w-24 truncate">{fmt(row.created_at)}</span>
                <span className={`w-16 text-center uppercase ${row.kind === 'consume' ? 'text-red-300' : row.kind === 'refresh' || row.kind === 'pack_purchase' || row.kind === 'grant' ? 'text-emerald-300' : 'text-white/50'}`}>
                  {row.kind}
                </span>
                <span className="w-16 text-white/50">{row.source}</span>
                <span className={`w-10 text-right ${row.delta >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                  {row.delta >= 0 ? '+' : ''}{row.delta}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const GRANT_PACKS = [
  { id: 'pack_25',  credits: 25,  label: 'Small · 25 credits' },
  { id: 'pack_75',  credits: 75,  label: 'Medium · 75 credits' },
  { id: 'pack_200', credits: 200, label: 'Large · 200 credits' },
];

const GRANT_REASON_PRESETS = [
  'Apology for outage',
  'Beta tester thank-you',
  'Missing renewal top-up',
  'Community contest prize',
  'Support goodwill',
];

function GrantPackForm({ userId, onGrant, onCancel, granting }) {
  const [packId, setPackId] = useState('pack_25');
  const [reason, setReason] = useState('Support goodwill');
  return (
    <div className="mt-3 rounded-xl border border-accent/40 bg-accent/5 p-3 space-y-2" data-testid={`grant-pack-form-${userId}`}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
        Goodwill grant · no charge to Stripe
      </p>
      <div className="grid grid-cols-3 gap-1.5">
        {GRANT_PACKS.map(p => (
          <button
            key={p.id}
            onClick={() => setPackId(p.id)}
            data-testid={`grant-choice-${p.id}`}
            className={`py-2 rounded-lg text-[11px] font-semibold border ${packId === p.id ? 'bg-accent text-black border-accent' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
          >
            +{p.credits}
          </button>
        ))}
      </div>
      <div>
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Reason presets</p>
        <div className="flex flex-wrap gap-1" data-testid={`grant-reason-presets-${userId}`}>
          {GRANT_REASON_PRESETS.map(preset => (
            <button
              key={preset}
              onClick={() => setReason(preset)}
              data-testid={`grant-reason-preset-${preset.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className={`px-2 py-1 rounded-full text-[10px] border transition ${reason === preset ? 'bg-accent/25 border-accent/60 text-accent' : 'bg-white/5 border-white/10 hover:bg-white/10 text-white/70'}`}
            >
              {preset}
            </button>
          ))}
        </div>
      </div>
      <input
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Or type a custom reason…"
        maxLength={80}
        data-testid={`grant-reason-${userId}`}
        className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-[11px] placeholder:text-muted-foreground focus:border-accent/60 outline-none"
      />
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          disabled={granting}
          data-testid={`grant-cancel-${userId}`}
          className="flex-1 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] text-white/70"
        >
          Cancel
        </button>
        <button
          onClick={() => onGrant(packId, reason)}
          disabled={granting || !reason.trim()}
          data-testid={`grant-confirm-${userId}`}
          className="flex-[2] py-1.5 rounded-lg bg-accent text-black text-[11px] font-semibold hover:bg-accent/90 disabled:opacity-60 flex items-center justify-center gap-1"
        >
          {granting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          Grant {GRANT_PACKS.find(p => p.id === packId)?.credits} credits
        </button>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent = false }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 ${accent ? 'bg-accent/10 border border-accent/30' : 'bg-white/5'}`}>
      <p className="text-[8px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`font-mono text-sm ${accent ? 'text-accent' : ''}`}>{value}</p>
    </div>
  );
}

// -----------------------------------------------------------------------
// Credits reconcile — support tool
// -----------------------------------------------------------------------
function CreditsReconcilePanel() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = () =>
    api.get('/admin/credits/reconcile/status')
      .then(r => { setStatus(r.data); setForbidden(false); })
      .catch(err => {
        if (err?.response?.status === 403) setForbidden(true);
      });

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/credits/reconcile');
      const label = data.refreshed
        ? `Refreshed ${data.refreshed} subscriber${data.refreshed === 1 ? '' : 's'}`
        : 'Sweep complete — nothing to refresh';
      toast.success(label);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reconcile failed');
    } finally {
      setRunning(false);
    }
  };

  if (forbidden) return null;

  const last = status?.last_run;
  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };
  const pending = status?.expired_pending ?? 0;

  return (
    <div className="rounded-3xl bg-card border border-white/5 p-4" data-testid="credits-reconcile-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Coins className="w-4 h-4 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Discovery Credits · Renewal Sweep</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Backstop for lost Stripe renewal webhooks. Runs hourly on its own — trigger manually to fix a stuck user right now.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-xl bg-black/25 border border-white/5 p-2.5" data-testid="credits-last-run">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Last run</p>
          <p className="font-mono text-sm mt-1">{fmtTime(last?.ran_at)}</p>
          {last?.source && (
            <p className="text-[9px] text-muted-foreground mt-0.5">
              via <span className="uppercase tracking-wider">{last.source}</span>
            </p>
          )}
        </div>
        <div className="rounded-xl bg-black/25 border border-white/5 p-2.5" data-testid="credits-last-refreshed">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Refreshed</p>
          <p className="font-mono text-sm mt-1">{last?.refreshed ?? 0}</p>
          {last?.errors ? (
            <p className="text-[9px] text-red-400 mt-0.5">{last.errors} error{last.errors === 1 ? '' : 's'}</p>
          ) : null}
        </div>
        <div className={`rounded-xl border p-2.5 ${pending > 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-black/25 border-white/5'}`}
             data-testid="credits-pending">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Expired pending</p>
          <p className={`font-mono text-sm mt-1 ${pending > 0 ? 'text-amber-300' : ''}`}>{pending}</p>
          <p className="text-[9px] text-muted-foreground mt-0.5">waiting for sweep</p>
        </div>
      </div>

      <button
        onClick={runNow}
        disabled={running}
        data-testid="credits-reconcile-run-btn"
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-accent text-black text-xs font-semibold hover:bg-accent/90 disabled:opacity-60 transition-colors"
      >
        {running ? (
          <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running sweep…</>
        ) : (
          <><RefreshCw className="w-3.5 h-3.5" /> Run reconcile now</>
        )}
      </button>

      {status?.runs?.length > 1 && (
        <details className="mt-3" data-testid="credits-run-history">
          <summary className="text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-white/70">
            Recent runs ({status.runs.length})
          </summary>
          <div className="mt-2 space-y-1">
            {status.runs.map((run, i) => (
              <div key={i} className="flex items-center justify-between text-[10px] font-mono py-1 border-b border-white/5 last:border-0">
                <span className="text-muted-foreground">{fmtTime(run.ran_at)}</span>
                <span className="text-[9px] uppercase text-white/50">{run.source}</span>
                <span className="text-accent">↻ {run.refreshed}</span>
                <span className="text-muted-foreground">/ {run.scanned}</span>
                {run.errors > 0 && <span className="text-red-400">✕ {run.errors}</span>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Trailhead backfill — one-shot policy migration (iter88 → 5/month non-comp)
// -----------------------------------------------------------------------
function TrailheadBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [applied, setApplied] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const dryRun = async () => {
    setRunning(true);
    setApplied(null);
    try {
      const { data } = await api.post('/admin/credits/backfill-trailhead', null, { params: { dry_run: true } });
      setPreview(data);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error(err?.response?.data?.detail || 'Dry-run failed');
    } finally { setRunning(false); }
  };

  const apply = async () => {
    if (!window.confirm(
      `Backfill ${preview?.refreshed ?? '?'} Trailhead users to 5 monthly credits?\n\n` +
      'This is idempotent — users already on the new policy are skipped. ' +
      'Runs in-band and writes an audit-log entry.'
    )) return;
    setRunning(true);
    try {
      const { data } = await api.post('/admin/credits/backfill-trailhead', null, { params: { dry_run: false } });
      setApplied(data);
      setPreview(null);
      toast.success(`Refreshed ${data.refreshed} of ${data.scanned} Trailhead users`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    } finally { setRunning(false); }
  };

  if (forbidden) return null;

  const last = applied || preview;

  return (
    <div className="rounded-3xl bg-card border border-white/5 p-4" data-testid="trailhead-backfill-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Trailhead · Monthly credits backfill</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            One-shot migration to the "5 credits monthly, non-compounding" free-tier policy. Idempotent — safe to re-run.
          </p>
        </div>
      </div>

      {last && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="rounded-xl bg-black/25 border border-white/5 p-2.5">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Scanned</p>
            <p className="font-mono text-sm mt-1" data-testid="trailhead-backfill-scanned">{last.scanned}</p>
          </div>
          <div className={`rounded-xl border p-2.5 ${last.refreshed > 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-black/25 border-white/5'}`}>
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground">
              {applied ? 'Refreshed' : 'Would refresh'}
            </p>
            <p className={`font-mono text-sm mt-1 ${last.refreshed > 0 ? 'text-emerald-300' : ''}`}
               data-testid="trailhead-backfill-refreshed">{last.refreshed}</p>
          </div>
          <div className="rounded-xl bg-black/25 border border-white/5 p-2.5">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Up-to-date</p>
            <p className="font-mono text-sm mt-1" data-testid="trailhead-backfill-skipped">{last.skipped_up_to_date}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={dryRun}
          disabled={running}
          data-testid="trailhead-backfill-dryrun-btn"
          className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold disabled:opacity-60 transition-colors border border-white/10"
        >
          {running && !applied ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning…</>
          ) : (
            <><Eye className="w-3.5 h-3.5" /> Preview (dry-run)</>
          )}
        </button>
        <button
          onClick={apply}
          disabled={running || !preview || applied || preview.refreshed === 0}
          data-testid="trailhead-backfill-apply-btn"
          className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 text-black text-xs font-semibold hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {running && applied === null && preview ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying…</>
          ) : applied ? (
            <><Check className="w-3.5 h-3.5" /> Done</>
          ) : (
            <><Sparkles className="w-3.5 h-3.5" /> Apply backfill</>
          )}
        </button>
      </div>

      {preview && !applied && preview.refreshed > 0 && (
        <p className="mt-2 text-[10px] text-amber-300/80" data-testid="trailhead-backfill-hint">
          {preview.refreshed} user{preview.refreshed === 1 ? ' is' : 's are'} out-of-date · click Apply to refresh
        </p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Refresh log — audit trail of past monthly credit refreshes
// -----------------------------------------------------------------------
function RefreshLogPanel() {
  const [rows, setRows] = useState(null);
  const [tier, setTier] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = (tierFilter = tier) => {
    setLoading(true);
    api.get('/admin/credits/refresh-log', { params: { limit: 30, tier: tierFilter || undefined } })
      .then(r => { setRows(r.data.log || []); setForbidden(false); })
      .catch(err => {
        if (err?.response?.status === 403) setForbidden(true);
        else toast.error('Failed to load refresh log');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(''); }, []); // eslint-disable-line

  if (forbidden) return null;

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const tiers = ['', 'trailhead', 'explorer', 'naturalist', 'pathfinder'];

  return (
    <div className="rounded-3xl bg-card border border-white/5 p-4" data-testid="refresh-log-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <ScrollText className="w-4 h-4 text-primary-light" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Credit Refresh Log</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Newest first · every monthly refresh writes a ledger row · filter by tier to isolate cadence
          </p>
        </div>
        <button
          onClick={() => load()}
          data-testid="refresh-log-reload"
          className="p-1.5 rounded-full hover:bg-white/5"
          title="Reload"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="refresh-log-filters">
        {tiers.map(tid => (
          <button
            key={tid || 'all'}
            onClick={() => { setTier(tid); load(tid); }}
            data-testid={`refresh-log-filter-${tid || 'all'}`}
            className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border transition ${
              tier === tid
                ? 'border-accent/60 bg-accent/10 text-accent'
                : 'border-white/10 bg-black/25 text-muted-foreground hover:text-white/80'
            }`}
          >
            {tid || 'all'}
          </button>
        ))}
      </div>

      {loading && !rows ? (
        <div className="text-center py-6 text-xs text-muted-foreground">Loading…</div>
      ) : (rows || []).length === 0 ? (
        <div className="text-center py-6 text-xs text-muted-foreground" data-testid="refresh-log-empty">
          No refreshes recorded yet.
        </div>
      ) : (
        <div className="rounded-xl border border-white/5 overflow-hidden">
          <div className="grid grid-cols-[110px_1fr_58px_74px] px-3 py-1.5 bg-white/5 text-[9px] uppercase tracking-widest text-muted-foreground">
            <span>When</span>
            <span>Explorer</span>
            <span className="text-right">Amount</span>
            <span className="text-right">Tier</span>
          </div>
          <div className="max-h-72 overflow-y-auto" data-testid="refresh-log-rows">
            {rows.map((row, i) => (
              <div key={row.ref || i}
                   data-testid="refresh-log-row"
                   className="grid grid-cols-[110px_1fr_58px_74px] items-center px-3 py-1.5 text-xs border-b border-white/5 last:border-0 hover:bg-white/5">
                <span className="font-mono text-[10px] text-muted-foreground">{fmtTime(row.created_at)}</span>
                <span className="min-w-0 truncate">
                  <span className="text-white/90">{row.user_name || '—'}</span>
                  <span className="text-muted-foreground text-[10px] ml-1.5">{row.user_email || ''}</span>
                </span>
                <span className="text-right font-mono text-emerald-300">+{row.amount}</span>
                <span className="text-right text-[10px] uppercase tracking-widest text-white/70">{row.tier || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl bg-card/60 border border-white/5 p-3" data-testid={testid}>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-mono text-2xl mt-1">{value ?? 0}</p>
    </div>
  );
}


// -----------------------------------------------------------------------
// Users
// -----------------------------------------------------------------------
function UsersPane({ me, has, onChange }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableRoles, setAvailableRoles] = useState([]);

  const load = () => {
    setLoading(true);
    const url = q ? `/admin/users?q=${encodeURIComponent(q)}` : '/admin/users?limit=100';
    api.get(url).then(r => setRows(r.data.users || [])).catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get('/admin/roles').then(r => setAvailableRoles(r.data.roles || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-3" data-testid="pane-users">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="input-user-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Search email or name…"
            className="w-full pl-9 pr-3 py-2 rounded-full bg-card/60 border border-white/10 text-sm outline-none focus:border-accent/60"
          />
        </div>
        <button onClick={load} data-testid="btn-user-refresh" className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-card/60 border border-white/10 flex items-center gap-1.5">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading ? <Loader2 className="w-4 h-4 animate-spin text-accent" /> : (
        <div className="rounded-3xl bg-card border border-white/5 divide-y divide-white/5 overflow-hidden">
          {rows.length === 0 && <p className="p-6 text-xs text-center text-muted-foreground">No users found</p>}
          {rows.map(u => (
            <button
              key={u.user_id}
              onClick={() => setSelected(u)}
              data-testid="user-row"
              className="w-full flex items-center gap-3 p-3 hover:bg-white/5 text-left"
            >
              {u.picture ? (
                <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-[11px] text-accent">
                  {(u.name || u.email || '?')[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm truncate">{u.name || 'Unnamed'}</span>
                  {u.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" />}
                  {u.is_banned && <Ban className="w-3.5 h-3.5 text-red-400" />}
                </div>
                <p className="text-[10px] text-muted-foreground font-mono truncate">{u.email}</p>
              </div>
              <div className="text-right space-y-1">
                <div className="flex flex-wrap gap-1 justify-end">
                  {(u.roles || []).slice(0, 2).map(r => (
                    <span key={r.role_id} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-400/20 text-emerald-200">{r.name}</span>
                  ))}
                </div>
                <p className="text-[9px] text-muted-foreground">Lv {u.level ?? 1}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {selected && (
          <UserEditorModal
            user={selected}
            me={me}
            roles={availableRoles}
            onClose={() => setSelected(null)}
            onSaved={() => { setSelected(null); load(); onChange(); }}
            has={has}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


function UserEditorModal({ user, me, roles, onClose, onSaved, has }) {
  const [selectedRoleIds, setSelectedRoleIds] = useState(user.role_ids || []);
  const [saving, setSaving] = useState(false);

  const target = user;
  const isBootstrap = me?.user?.email && target.email &&
    (target.email.toLowerCase() === me?.user?.email?.toLowerCase() && me?.is_bootstrap_admin) ||
    (target.is_bootstrap_admin === true);

  const toggleRole = (rid) => {
    setSelectedRoleIds(prev => prev.includes(rid) ? prev.filter(x => x !== rid) : [...prev, rid]);
  };

  const saveRoles = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${target.user_id}/roles`, { role_ids: selectedRoleIds });
      toast.success('Roles updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update roles');
    } finally { setSaving(false); }
  };

  const doAction = async (path, body = {}, label = 'Done') => {
    try {
      const r = await api.post(`/admin/users/${target.user_id}${path}`, body);
      toast.success(label);
      if (r.data?.temp_password) {
        // Show reset password
        prompt(`Temp password for ${target.email}:`, r.data.temp_password);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  // Presets for the most common founder / community badges. Custom badges
  // are handled by the "Custom…" option below.
  const BADGE_PRESETS = [
    { id: 'founder', name: 'Founding Explorer', desc: 'Joined the WildQuest founding cohort.' },
    { id: 'trail_blazer', name: 'Trail Blazer', desc: 'Brought a new explorer to WildQuest.' },
    { id: 'master_naturalist', name: 'Master Naturalist', desc: 'Documented across every category.' },
  ];
  const grantBadge = async (preset) => {
    let body = preset;
    if (!body) {
      const id = prompt('Badge id (kebab-case)?');
      if (!id) return;
      const name = prompt('Badge display name?', id);
      if (!name) return;
      const desc = prompt('Short description (optional)?', '') || '';
      body = { id: id.trim(), name: name.trim(), desc };
    }
    try {
      await api.post('/admin/badges/grant', { user_id: target.user_id, ...body });
      toast.success(`Awarded "${body.name}"`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grant failed');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="user-editor-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl bg-[#0e1613] border border-white/10 p-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-accent">User</p>
            <h3 className="font-serif text-xl">{target.name || 'Unnamed'}</h3>
            <p className="text-[10px] text-muted-foreground font-mono">{target.email}</p>
            {isBootstrap && (
              <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-400/30 text-[9px] uppercase tracking-widest text-amber-300">
                <Crown className="w-2.5 h-2.5" /> Bootstrap Admin (protected)
              </span>
            )}
          </div>
          <button onClick={onClose} data-testid="btn-modal-close" className="p-1 rounded-full bg-white/5 hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Roles</p>
            <div className="flex flex-wrap gap-1.5" data-testid="role-toggle-row">
              {roles.map(r => {
                const active = selectedRoleIds.includes(r.role_id);
                return (
                  <button
                    key={r.role_id}
                    onClick={() => toggleRole(r.role_id)}
                    disabled={!has('admins.manage')}
                    data-testid={`role-toggle-${r.key}`}
                    className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                      active ? 'border-accent/60 bg-accent/10 text-accent'
                        : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >{r.name}</button>
                );
              })}
            </div>
            {has('admins.manage') && (
              <button
                onClick={saveRoles}
                disabled={saving}
                data-testid="btn-save-roles"
                className="mt-3 text-[10px] uppercase tracking-widest px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 flex items-center gap-1.5 disabled:opacity-50"
              >{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Roles</button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/5">
            {has('users.verify') && !target.is_verified && (
              <button onClick={() => doAction('/verify', {}, 'Verified')} data-testid="btn-verify"
                className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 flex items-center justify-center gap-1.5">
                <BadgeCheck className="w-3 h-3" /> Verify
              </button>
            )}
            {has('users.grant_premium') && !target.is_premium && (
              <button onClick={() => doAction('/grant-premium', {}, 'Premium granted')} data-testid="btn-grant-premium"
                className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-200 flex items-center justify-center gap-1.5">
                <Crown className="w-3 h-3" /> Grant Premium
              </button>
            )}
            {has('users.reset_password') && (
              <button onClick={() => doAction('/reset-password', {}, 'Password reset')} data-testid="btn-reset-password"
                className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 flex items-center justify-center gap-1.5">
                <KeyRound className="w-3 h-3" /> Reset Password
              </button>
            )}
            {has('users.suspend') && !isBootstrap && (
              target.is_active === false ? (
                <button onClick={() => doAction('/unsuspend', {}, 'Unsuspended')} data-testid="btn-unsuspend"
                  className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" /> Unsuspend
                </button>
              ) : (
                <button onClick={() => {
                  const reason = prompt('Suspension reason?');
                  if (reason !== null) doAction('/suspend', { reason }, 'Suspended');
                }} data-testid="btn-suspend"
                  className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-red-500/10 border border-red-400/30 text-red-300 flex items-center justify-center gap-1.5">
                  <Ban className="w-3 h-3" /> Suspend
                </button>
              )
            )}
            {has('users.suspend') && !isBootstrap && !target.is_banned && (
              <button onClick={() => {
                const reason = prompt('Ban reason?');
                if (reason !== null) doAction('/ban', { reason }, 'Banned');
              }} data-testid="btn-ban"
                className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-red-500/15 border border-red-400/40 text-red-200 flex items-center justify-center gap-1.5">
                <Ban className="w-3 h-3" /> Ban User
              </button>
            )}
          </div>

          {has('system.settings') && (
            <div className="pt-3 border-t border-white/5 space-y-2" data-testid="admin-badge-grant-panel">
              <p className="text-[10px] uppercase tracking-widest text-accent">Grant a badge</p>
              {(target.badges?.length || 0) > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="admin-badge-current">
                  {target.badges.map((b, i) => (
                    <span key={i}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/40 text-amber-200">
                      {b.name || b.id}
                    </span>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {BADGE_PRESETS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => grantBadge(p)}
                    data-testid={`btn-grant-badge-${p.id}`}
                    className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-200 flex items-center justify-center gap-1.5 hover:bg-amber-500/20">
                    <Crown className="w-3 h-3" /> {p.name}
                  </button>
                ))}
                <button
                  onClick={() => grantBadge(null)}
                  data-testid="btn-grant-badge-custom"
                  className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 flex items-center justify-center gap-1.5 hover:bg-white/10">
                  Custom…
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}


// -----------------------------------------------------------------------
// Roles + Permissions
// -----------------------------------------------------------------------
function RolesPane({ me, has }) {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/roles').then(r => r.data.roles || []),
      api.get('/admin/permissions').then(r => r.data.permissions || []),
    ]).then(([rs, ps]) => { setRoles(rs); setPerms(ps); })
      .catch(() => toast.error('Failed to load roles'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const savePerms = async (role, nextPerms) => {
    try {
      await api.patch(`/admin/roles/${role.role_id}`, { permissions: nextPerms });
      toast.success('Role updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const createRole = async () => {
    const name = prompt('New role name?');
    if (!name) return;
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    try {
      await api.post('/admin/roles', { name, key, permissions: [] });
      toast.success('Role created');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteRole = async (r) => {
    if (r.is_system) return toast.error('System roles cannot be deleted');
    if (!confirm(`Delete role "${r.name}"?`)) return;
    try {
      await api.delete(`/admin/roles/${r.role_id}`);
      toast.success('Role deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-accent" />;

  return (
    <div className="space-y-3" data-testid="pane-roles">
      {has('roles.manage') && (
        <button onClick={createRole} data-testid="btn-create-role"
          className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-accent text-black font-semibold flex items-center gap-1.5 hover:brightness-110">
          <Plus className="w-3 h-3" /> New Role
        </button>
      )}
      <div className="space-y-3">
        {roles.map(r => (
          <RoleCard key={r.role_id} role={r} allPerms={perms}
                    editing={editing === r.role_id}
                    onEdit={() => setEditing(r.role_id)}
                    onCancel={() => setEditing(null)}
                    onSave={(next) => { setEditing(null); savePerms(r, next); }}
                    onDelete={() => deleteRole(r)}
                    has={has}
          />
        ))}
      </div>
    </div>
  );
}


function RoleCard({ role, allPerms, editing, onEdit, onCancel, onSave, onDelete, has }) {
  const [checked, setChecked] = useState(new Set(role.permissions || []));
  useEffect(() => { setChecked(new Set(role.permissions || [])); }, [role.role_id, editing]);
  const toggle = (p) => {
    const next = new Set(checked);
    if (next.has(p)) next.delete(p); else next.add(p);
    setChecked(next);
  };
  const canEdit = has('roles.manage') && !role.is_locked;

  const groups = useMemo(() => {
    const g = {};
    (allPerms || []).forEach(p => {
      const bucket = p.key?.split('.')[0] || 'misc';
      (g[bucket] = g[bucket] || []).push(p);
    });
    return g;
  }, [allPerms]);

  return (
    <div className="rounded-3xl bg-card border border-white/5 p-4" data-testid={`role-card-${role.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-serif text-lg leading-tight">{role.name}</p>
          <p className="text-[10px] font-mono text-muted-foreground">{role.key}</p>
          {role.description && <p className="text-xs text-muted-foreground mt-1">{role.description}</p>}
          <div className="flex gap-1 mt-2 flex-wrap">
            {role.is_system && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-amber-500/10 border border-amber-400/30 text-amber-200">System</span>}
            {role.is_locked && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-red-500/10 border border-red-400/30 text-red-200">Locked</span>}
            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/60">
              {(role.permissions || []).length === 1 && role.permissions[0] === '*' ? 'ALL PERMS' : `${(role.permissions || []).length} perms`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {canEdit && !editing && (
            <button onClick={onEdit} data-testid={`btn-edit-role-${role.key}`}
              className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10">Edit</button>
          )}
          {canEdit && editing && (
            <>
              <button onClick={() => onSave(Array.from(checked))} data-testid={`btn-save-role-${role.key}`}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 flex items-center gap-1"><Save className="w-3 h-3" /> Save</button>
              <button onClick={onCancel} data-testid={`btn-cancel-role-${role.key}`}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Cancel</button>
            </>
          )}
          {canEdit && !role.is_system && (
            <button onClick={onDelete} data-testid={`btn-delete-role-${role.key}`}
              className="p-1.5 rounded-full bg-red-500/10 border border-red-400/20 text-red-300"><Trash2 className="w-3 h-3" /></button>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-4 space-y-3" data-testid={`role-perms-${role.key}`}>
          {Object.entries(groups).sort(([a],[b]) => a.localeCompare(b)).map(([group, perms]) => (
            <div key={group}>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">{group}</p>
              <div className="flex flex-wrap gap-1.5">
                {perms.map(p => (
                  <button key={p.key} onClick={() => toggle(p.key)}
                    data-testid={`perm-toggle-${p.key}`}
                    className={`text-[10px] font-mono px-2 py-1 rounded-md border ${
                      checked.has(p.key) ? 'border-accent/60 bg-accent/10 text-accent' : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
                    }`}
                    title={p.description}
                  >{p.key}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------
// Audit log
// -----------------------------------------------------------------------
function AuditPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: '150' });
    if (q) p.set('q', q);
    if (action) p.set('action', action);
    api.get(`/admin/audit-log?${p.toString()}`)
      .then(r => setRows(r.data.entries || []))
      .catch(() => toast.error('Failed to load audit log'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="pane-audit">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          data-testid="input-audit-q"
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
          placeholder="Search…"
          className="flex-1 px-3 py-2 rounded-full bg-card/60 border border-white/10 text-sm outline-none focus:border-accent/60"
        />
        <input
          data-testid="input-audit-action"
          value={action} onChange={e => setAction(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
          placeholder="Action (e.g. user.suspend)"
          className="flex-1 px-3 py-2 rounded-full bg-card/60 border border-white/10 text-sm font-mono outline-none focus:border-accent/60"
        />
        <button onClick={load} data-testid="btn-audit-refresh"
          className="text-[10px] uppercase tracking-widest px-3 py-2 rounded-full bg-card/60 border border-white/10 flex items-center gap-1.5">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading ? <Loader2 className="w-4 h-4 animate-spin text-accent" /> : (
        <div className="rounded-3xl bg-card border border-white/5 divide-y divide-white/5 overflow-hidden">
          {rows.length === 0 && <p className="p-6 text-xs text-center text-muted-foreground">No audit entries</p>}
          {rows.map((e, i) => (
            <div key={e.audit_id || i} data-testid="audit-row" className="p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-accent">{e.action}</span>
                <span className="text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                by <span className="text-white/80">{e.actor_email || e.actor_id}</span>
                {e.target_user_id && <> · target <span className="text-white/80">{e.target_user_id}</span></>}
              </p>
              {(e.before || e.after) && (
                <details className="mt-1">
                  <summary className="text-[10px] cursor-pointer text-muted-foreground">Payload</summary>
                  <pre className="text-[10px] font-mono bg-black/30 p-2 rounded mt-1 overflow-x-auto">
{JSON.stringify({ before: e.before, after: e.after }, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
